// Re-export types
export * from './types'

// Import journey data
import { meetingToDone } from './meeting-to-done'
import { rambleToAction } from './ramble-to-action'
import { practiceGrowth } from './practice-growth'
import { referralFlywheel } from './referral-flywheel'
import { portal } from './portal'
import { headcountToHeadroom } from './headcount-to-headroom'
import { reclaimWeekends } from './reclaim-weekends'
import { adviserOs } from './adviser-os'
import type { JourneyPage, JourneyCard, Testimonial } from './types'

// All journey pages
export const journeyPages: Record<string, JourneyPage> = {
  'meeting-to-done': meetingToDone,
  'ramble-to-action': rambleToAction,
  'practice-growth': practiceGrowth,
  'referral-flywheel': referralFlywheel,
  'portal': portal,
  'headcount-to-headroom': headcountToHeadroom,
  'reclaim-weekends': reclaimWeekends,
  'adviser-os': adviserOs,
}

// Journey cards for the home page selector
export const journeyCards: JourneyCard[] = [
  {
    slug: 'meeting-to-done',
    colorClass: 'meeting',
    icon: 'video',
    title: 'Meeting to Done',
    tagline: 'Client meeting at 10am. Report by lunch.',
    description:
      "Follow a retirement planning meeting as it transforms into transcription, research, and a suitability report, all before you've finished your coffee.",
  },
  {
    slug: 'ramble-to-action',
    colorClass: 'ramble',
    icon: 'microphone',
    title: 'Ramble to Action',
    tagline: 'Speak your thoughts. Watch them happen.',
    description:
      'Dictate into your Apple Watch or CarPlay while driving. By the time you arrive, your thoughts have become tasks, notes, and client follow-ups.',
  },
  {
    slug: 'practice-growth',
    colorClass: 'growth',
    icon: 'chart',
    title: '80 Clients to Exit',
    tagline: 'From lifestyle practice to 7-figure sale.',
    description:
      "80 clients, 60-hour weeks, no exit plan. Five years later: 175 clients, £88M AUM, £2.3M exit. Some do it in 3 years. Others take 7. The maths works either way.",
  },
  {
    slug: 'referral-flywheel',
    colorClass: 'referral',
    icon: 'pound',
    title: 'Price to Value',
    tagline: 'Your advice is valuable. Help clients see it.',
    description:
      'Transform MiFID II cost disclosures into value demonstrations. Show clients exactly what they get for their fees with beautiful reports and calculators.',
  },
  {
    slug: 'portal',
    colorClass: 'portal',
    icon: 'users',
    title: 'Annual to Always',
    tagline: 'Coming soon: your client portal, always on.',
    description:
      'Transform the annual review into an ongoing conversation. Clients see their wealth, documents, and progress 24/7. Engagement that builds loyalty.',
  },
  {
    slug: 'headcount-to-headroom',
    colorClass: 'headroom',
    icon: 'presentation',
    title: 'Headcount to Headroom',
    tagline: 'Grow your practice, not your admin.',
    description:
      'Create 40% more capacity without hiring. No more weekend catch-ups or Sunday dread - just sustainable growth and time for the people who matter.',
  },
]

// Testimonials for various sections
export const testimonials: Testimonial[] = [
  {
    quote:
      'We saw an immediate reduction in the time it takes to process a client. Being able to dictate notes and have the platform generate clear, consistent file notes has made my life so much easier.',
    author: 'Mike Haupt',
    role: 'Director',
    company: 'Haupt & Haupt FS',
  },
  {
    quote:
      'We have significantly reduced our workload and become much more efficient in just a few weeks. WealthSpace has streamlined our existing processes, making them faster and more effective.',
    author: 'Karen Fussell',
    role: 'Ops Director',
    company: 'OWM (Newbury)',
  },
  {
    quote:
      'Significant improvements in efficiency and client service capabilities since integrating WealthSpace. Implementation was simple with major improvements from day one.',
    author: 'Deane Anderson',
    role: 'Director',
    company: 'Highclere Wealth',
  },
  {
    quote:
      'WealthSpace offers flexibility and keeps advisers in control. Support is there if you need it, but you can adapt firm-specific elements like report templates at your own pace.',
    author: 'Dan Wiltshire',
    role: 'Independent Financial Planner',
    company: 'Wiltshire Wealth',
  },
]

// Helper function to get a journey by slug
export function getJourneyBySlug(slug: string): JourneyPage | undefined {
  return journeyPages[slug]
}

// Helper function to check if a journey exists
export function journeyExists(slug: string): boolean {
  return slug in journeyPages
}
